import React, { useState } from "react"
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    Avatar,
    LinearProgress,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AdxToolTip from 'components/custom/AdxToolTip'
import { isHiddenProjectTrackerStaff } from '../pages/project-tracker/helpers/hiddenStaff'

const truncateText = (str, max) => {
    const s = String(str ?? "")
    if (s.length <= max) return s
    return s.slice(0, max) + "…"
}

const safeDate = (value) => {
    if (!value) return null
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

const fmtDate = (value) => {
    const d = safeDate(value)
    if (!d) return "-"
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(d)
}

const daysDiffFromToday = (value) => {
    const d = safeDate(value)
    if (!d) return null

    const today = new Date()
    const a = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
    const b = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

    return Math.round((b - a) / (1000 * 60 * 60 * 24))
}

const computeDue = (deadlineValue) => {
    const diff = daysDiffFromToday(deadlineValue)

    if (diff == null) {
        return {
            dotColor: "rgba(0,0,0,0.25)",
            dateText: "-",
            isOverdue: false,
            daysLeftText: "No deadline set",
        }
    }

    if (diff < 0) {
        const days = Math.abs(diff)
        return {
            dotColor: "#e53935",
            dateText: fmtDate(deadlineValue),
            isOverdue: true,
            daysLeftText: `${days} day${days === 1 ? "" : "s"} overdue`,
        }
    }

    if (diff <= 7) {
        return {
            dotColor: "#f9a825",
            dateText: fmtDate(deadlineValue),
            isOverdue: false,
            daysLeftText: diff === 0 ? "Due today" : `${diff} day${diff === 1 ? "" : "s"} left`,
        }
    }

    return {
        dotColor: "#43a047",
        dateText: fmtDate(deadlineValue),
        isOverdue: false,
        daysLeftText: `${diff} day${diff === 1 ? "" : "s"} left`,
    }
}

const buildTeamInsightsFromMembers = (members = []) => {
    return members
        .filter((member) => String(member?.role ?? "").toLowerCase() === "team_member")
        .filter((member) => !isHiddenProjectTrackerStaff(member?.staff ?? member))
        .map((member) => {
            const stats = member?.member_deliverables_stats || {}
            const staff = member?.staff || {}
            const deliverables = member?.deliverables ?? member?.member_deliverables ?? []

            return {
                id: member?.id ?? staff?.id ?? staff?.user_id ?? staff?.name,
                name: staff?.name ?? "Unknown",
                profileImageUrl:
                    staff?.profile_image_url ||
                    staff?.staff_profile_image?.profile_image_url ||
                    null,
                value: Number(stats?.completed_value ?? 0),
                total: Number(stats?.total_value ?? 0),
                pending: Number(stats?.pending_value ?? 0),
                display:
                    stats?.display ||
                    `${Number(stats?.completed_value ?? 0)}/${Number(stats?.total_value ?? 0)}`,
                overflowAllowed: deliverables.some((d) => d.overflow_allowed === true ),
            }
        })
}

function StatBar({ leftIcon, valueText, percent, trackBg, barBg, height = 18, tooltip }) {
    return (
        <Stack direction="row" alignItems="center" gap={2}>
            <Box sx={{ width: 30, display: "flex", justifyContent: "center" }}>
                {tooltip ? (
                    <Tooltip title={tooltip} arrow placement="bottom">
                        <Box sx={{ display: "flex" }}>{leftIcon}</Box>
                    </Tooltip>
                ) : leftIcon}
            </Box>

            <Box sx={{ flex: 1 }}>
                <Box
                    sx={{
                        position: "relative",
                        borderRadius: 999,
                        overflow: "hidden",
                        backgroundColor: trackBg,
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, percent || 0))}
                        sx={{
                            height,
                            backgroundColor: "transparent",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 999,
                                backgroundColor: barBg,
                            },
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            color: "rgba(0,0,0,0.75)",
                        }}
                    >
                        {valueText}
                    </Box>
                </Box>
            </Box>
        </Stack>
    )
}

function TaskList({ task }) {
    const taskNameRaw = task?.name || task?.label || "Untitled Task"
    const assigneeNameRaw = task?.assigned_to?.name || task?.assignee || "-"
    const assigneeImg =
        task?.assigned_to?.profile_image_url ||
        task?.assigned_to?.image_url ||
        task?.assigned_to?.staff_profile_image?.profile_image_url ||
        task?.assigned_to?.staff_profile_image?.profile_image ||
        null

    const isCompleted = String(task?.status || "").toLowerCase() === "completed"

    const taskNameFallback = truncateText(taskNameRaw, 15)
    const assigneeName = truncateText(assigneeNameRaw, 10)

    {/* Task Tooltips data */ }
    const renderTaskTooltip = () => (
        <Box sx={{
            p: 2
        }}>
            {/* Header: Task name and assign to avatar */}
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {task?.name || task?.label || "Untitled Task"}
                </Typography>
                <Avatar
                    src={assigneeImg || undefined}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                    sx={{ width: 28, height: 28, bgcolor: "#F5C542", color: "black", fontSize: 13 }}
                >
                    {(assigneeNameRaw || "?")[0]}
                </Avatar>
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {/* Assignee */}
            <Typography sx={{ fontSize: 13, mb: 0.75 }}>
                <strong>Assignee:</strong>{" "}
                <span style={{ color: "rgba(0,0,0,0.55)" }}>{assigneeNameRaw || "-"}</span>
            </Typography>

            {/* Due Date */}
            <Typography sx={{ fontSize: 13, mb: 0.75 }}>
                <strong>Due Date:</strong>{" "}
                <span style={{ color: "rgba(0,0,0,0.55)" }}>{fmtDate(task?.due_date)}</span>
            </Typography>

            {/* To Do Date */}
            <Typography sx={{ fontSize: 13, mb: 0.75 }}>
                <strong>To Do Date:</strong>{" "}
                <span style={{ color: "rgba(0,0,0,0.55)" }}>{fmtDate(task?.scheduled_date)}</span>
            </Typography>

            {/* Deliverable type */}
            <Typography sx={{ fontSize: 13, mb: 0.75 }}>
                <strong>Deliverable:</strong>{" "}
                <span style={{ color: "rgba(0,0,0,0.55)" }}>
                    {task?.project_member_deliverable?.deliverable_type || "-"}
                </span>
            </Typography>

            {/* Deliverable value */}
            <Typography sx={{ fontSize: 13 }}>
                <strong>Value:</strong>{" "}
                <span style={{ color: "rgba(0,0,0,0.55)" }}>
                    {task?.value != null ? task.value : "-"}
                </span>
            </Typography>
        </Box>
    )

    return (
        <AdxToolTip
            title={renderTaskTooltip()}
            placement="right"
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: '#ffffff',
                        color: '#000',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: 3,
                        p: 0,
                        maxWidth: 280,
                        border: '1px solid #e5e5e5',
                    },
                },
            }}
        >
            <Box
                sx={{
                    border: "1px solid rgba(0,0,0,0.14)",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    backgroundColor: isCompleted ? "rgba(0, 200, 83, 0.12)" : "white",
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                "@supports (-webkit-line-clamp: 1)": {
                                    whiteSpace: "normal",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 1,
                                    overflow: "hidden",
                                },
                            }}
                        >
                            {taskNameFallback}
                        </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0, justifyContent: "flex-end" }}>

                        {task?.value != null && (

                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "rgba(0,0,0,0.75)" }}>
                                {task.value}
                            </Typography>
                        )}

                        <Avatar
                            src={assigneeImg || undefined}
                            imgProps={{ referrerPolicy: "no-referrer" }}
                            sx={{
                                width: 28,
                                height: 28,
                                bgcolor: "#F5C542",
                                color: "black",
                                fontSize: 14,
                            }}
                        >
                            {(assigneeNameRaw || "?")[0]}
                        </Avatar>

                        {/* <Typography sx={{ fontWeight: 700 }} noWrap>
                        {assigneeName}
                    </Typography> */}
                    </Stack>

                    <Box sx={{ width: 0, display: "flex", justifyContent: "flex-end" }}>
                        {/* {isCompleted ? <CheckCircleIcon sx={{ color: "#2ecc71" }} /> : null} */}
                        {null}
                    </Box>
                </Stack>
            </Box>
        </AdxToolTip>
    )
}

function NoTasksYetCard() {
    return (
        <Box
            sx={{
                border: "1px solid rgba(0,0,0,0.14)",
                borderRadius: 3,
                p: 3,
                backgroundColor: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 1,
            }}
        >
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path
                        d="M25.6663 18.666V10.666C25.6663 6.89535 25.6663 5.00868 24.4943 3.83802C23.3237 2.66602 21.437 2.66602 17.6663 2.66602H12.333C8.56234 2.66602 6.67567 2.66602 5.50501 3.83802C4.33301 5.00868 4.33301 6.89535 4.33301 10.666V21.3327C4.33301 25.1033 4.33301 26.99 5.50501 28.1607C6.67567 29.3327 8.56234 29.3327 12.333 29.3327H17.6663"
                        stroke="#8A8A8A"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M9.66602 19.9993H14.9993M9.66602 14.666H20.3327M28.3327 22.666L24.9993 25.9993M24.9993 25.9993L21.666 29.3327M24.9993 25.9993L28.3327 29.3327M24.9993 25.9993L21.666 22.666M19.666 2.66602H10.3327C10.3327 4.55135 10.3327 5.49402 10.9193 6.07935C11.5047 6.66602 12.4473 6.66602 14.3327 6.66602H15.666C17.5513 6.66602 18.494 6.66602 19.0793 6.07935C19.666 5.49402 19.666 4.55135 19.666 2.66602Z"
                        stroke="#8A8A8A"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>

            <Typography variant="subtitle1" sx={{ color: "#3A3A3A", fontWeight: 500 }}>
                No tasks yet
            </Typography>

            <Typography variant="body1" sx={{ color: "#3A3A3A", fontWeight: 400 }}>
                Team members will add tasks to start tracking progress.
            </Typography>
        </Box>
    )
}

function TaskProjectCard({
    project,
    canEdit = false,
    canDuplicate = false,
    onEdit,
    onDuplicate,
    onMarkComplete,
    sortTasks = false,
}) {
    const [showMoreTasks, setShowMoreTasks] = useState(false)
    const projectName = project?.name || "Untitled Project"
    const startDate = project?.start_date || project?.startDate || null
    const deadline = project?.deadline || project?.due?.date || null
    const due = project?.due
        ? {
            dotColor: project?.due?.dotColor ?? "rgba(0,0,0,0.25)",
            dateText: project?.due?.date ?? project?.due?.dateText ?? "-",
            isOverdue: !!project?.due?.isOverdue,
            daysLeftText: project?.due?.daysLeftText ?? "-",
        }
        : computeDue(deadline)

    const stats = project?.team_deliverable_stats || {}

    const teamCompletedValue = Number(stats?.completed_value ?? 0)
    const teamPendingValue = Number(stats?.pending_value ?? 0)
    const teamTotalValue = Number(stats?.total_value ?? 0)

    const firstStatText =
        stats?.display || `${teamCompletedValue}/${teamTotalValue}`

    const firstStatPercent =
        teamTotalValue > 0 ? (teamCompletedValue / teamTotalValue) * 100 : 0

    const thirdStatValue = Math.max(
        0,
        teamTotalValue - teamCompletedValue - teamPendingValue
    )

    // const tasks = Array.isArray(project?.tasks) ? project.tasks : []
    const rawTasks = Array.isArray(project?.tasks) ? project.tasks : []
    const tasks = sortTasks
        ? [...rawTasks].sort((a, b) => {
            const aCompleted = String(a?.status || "").toLowerCase() === "completed"
            const bCompleted = String(b?.status || "").toLowerCase() === "completed"
            if (aCompleted !== bCompleted) return aCompleted ? 1 : -1
            const aDate = a?.due_date ? new Date(a.due_date).getTime() : Infinity
            const bDate = b?.due_date ? new Date(b.due_date).getTime() : Infinity
            return aDate - bDate
        })
        : rawTasks
    const members = Array.isArray(project?.members) ? project.members : []
    const teamInsights =
        Array.isArray(project?.teamInsights) && project.teamInsights.length
            ? project.teamInsights
            : buildTeamInsightsFromMembers(members)

    const isAlreadyCompleted = String(project?.status ?? "").toLowerCase() === "completed"
    const showMarkCompleteAction = Boolean(canEdit && !isAlreadyCompleted)
    const showEditAction = Boolean(canEdit && !isAlreadyCompleted)
    const showDuplicateAction = Boolean(canDuplicate)
    const showActions = showMarkCompleteAction || showEditAction || showDuplicateAction

    const renderProjectInfo = (project) => (
        <Box sx={{ p: 2 }}>
            {/* <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                Project Name
            </Typography> */}
            <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>
                {project?.name || "Untitled Project"}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            {/* Objective Section */}
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={0.5}
                color="text.secondary"
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
            >
                Objective
            </Typography>
            <Typography
                variant="body2"
                mb={2}
                sx={{
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color: project.objective ? 'text.primary' : 'text.disabled'
                }}
            >
                {project.objective || 'N/A'}
            </Typography>

            {/* Description Section */}
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={0.5}
                color="text.secondary"
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
            >
                Description
            </Typography>
            <Typography
                variant="body2"
                mb={2}
                sx={{
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color: project.background ? 'text.primary' : 'text.disabled'
                }}
            >
                {project.background || 'N/A'}
            </Typography>

            {/* Link Section */}
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={0.5}
                color="text.secondary"
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
            >
                Link
            </Typography>
            {project.related_links?.length > 0 ? (
                project.related_links.map((url, i) => (
                    <Typography
                        key={i}
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                            fontSize: '12px',
                            color: '#2F80ED',
                            textDecoration: 'none',
                            mb: 2,
                            wordBreak: 'break-all',
                            display: 'block',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {url}
                    </Typography>
                ))
            ) : (
                <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.disabled', mb: 2 }}>
                    N/A
                </Typography>
            )}

            {/* Dates Section */}
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={0.5}
                mt={2}
                color="text.secondary"
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
            >
                Start Date
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.primary', mb: 2 }}>
                {fmtDate(project?.start_date)}
            </Typography>
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={0.5}
                color="text.secondary"
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
            >
                Due Date
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'text.primary' }}>
                {fmtDate(project?.deadline)}
            </Typography>
        </Box>
    )

    return (
        <Box
            sx={{
                borderRadius: '10px',
                border: "1px solid #e5e5e5",
                backgroundColor: "#ffffff",
                p: 2,
            }}>

            <Card
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: "1px solid #e5e5e5",
                    backgroundColor: "#ffffff",
                }}
            >
                <CardContent
                    sx={{
                        px: 3,
                        py: 3,
                        backgroundColor: "#ffffff",
                        "&:last-child": {
                            pb: 3
                        }
                    }}>
                    <Stack sx={{ mb: 1.25, pt: showActions ? 0 : 1, pb: showActions ? 0 : 1 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            gap={0}
                            sx={{ minHeight: 32 }}
                        >
                            {showActions && (
                                <>
                                    <Box>
                                        {showMarkCompleteAction && (
                                            <Tooltip title="Mark as completed" arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onMarkComplete?.(project)
                                                        }}
                                                        sx={{ color: "rgba(0,0,0,0.7" }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
                                                            <g clipPath="url(#clip0_2068_7039)">
                                                                <path d="M4.26654 7.99987L7.46654 10.6665L11.7332 5.3332M7.99987 15.4665C7.01933 15.4665 6.0484 15.2734 5.1425 14.8982C4.2366 14.5229 3.41348 13.9729 2.72014 13.2796C2.0268 12.5863 1.4768 11.7631 1.10157 10.8572C0.726334 9.95134 0.533203 8.98041 0.533203 7.99987C0.533203 7.01933 0.726334 6.0484 1.10157 5.1425C1.4768 4.2366 2.0268 3.41348 2.72014 2.72014C3.41348 2.0268 4.2366 1.4768 5.1425 1.10157C6.0484 0.726334 7.01933 0.533203 7.99987 0.533203C9.98015 0.533203 11.8793 1.31987 13.2796 2.72014C14.6799 4.12041 15.4665 6.01959 15.4665 7.99987C15.4665 9.98015 14.6799 11.8793 13.2796 13.2796C11.8793 14.6799 9.98015 15.4665 7.99987 15.4665Z" stroke="#8a8a8a" stroke-width="1.5" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_2068_7039">
                                                                    <rect width="16" height="16" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        {showEditAction && (
                                            <Tooltip title="Edit project" arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onEdit?.(project)
                                                        }}
                                                        sx={{ color: "rgba(0,0,0,0.7)" }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <g clipPath="url(#clip0_2068_7035)">
                                                                <path d="M15.556 16.6673H3.33377V4.4451H10.6782L11.7893 3.33398H3.33377C3.03908 3.33398 2.75647 3.45105 2.54809 3.65942C2.33972 3.86779 2.22266 4.15041 2.22266 4.4451V16.6673C2.22266 16.962 2.33972 17.2446 2.54809 17.453C2.75647 17.6614 3.03908 17.7784 3.33377 17.7784H15.556C15.8507 17.7784 16.1333 17.6614 16.3417 17.453C16.55 17.2446 16.6671 16.962 16.6671 16.6673V8.33398L15.556 9.4451V16.6673Z" fill="#2666BE" />
                                                                <path d="M18.6284 3.24444L16.7561 1.37222C16.673 1.2889 16.5743 1.2228 16.4656 1.1777C16.357 1.13259 16.2405 1.10937 16.1228 1.10938C16.0051 1.10938 15.8886 1.13259 15.7799 1.1777C15.6713 1.2228 15.5725 1.2889 15.4895 1.37222L7.8728 9.03333L7.25613 11.7056C7.22986 11.8351 7.23262 11.9688 7.26422 12.0972C7.29581 12.2255 7.35546 12.3452 7.43886 12.4478C7.52227 12.5503 7.62736 12.6331 7.74658 12.6901C7.8658 12.7472 7.99618 12.7771 8.12835 12.7778C8.19667 12.7853 8.26559 12.7853 8.33391 12.7778L11.0284 12.1833L18.6284 4.51111C18.7117 4.42802 18.7778 4.32931 18.8229 4.22063C18.868 4.11195 18.8912 3.99544 18.8912 3.87777C18.8912 3.76011 18.868 3.6436 18.8229 3.53492C18.7778 3.42624 18.7117 3.32753 18.6284 3.24444ZM10.4506 11.1556L8.41724 11.6056L8.88946 9.58889L14.6228 3.81666L16.1895 5.38333L10.4506 11.1556ZM16.8172 4.75555L15.2506 3.18888L16.1117 2.31111L17.6895 3.88889L16.8172 4.75555Z" fill="#2666BE" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_2068_7035">
                                                                    <rect width="20" height="20" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        {showDuplicateAction && (
                                            <Tooltip title="Duplicate project" arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onDuplicate?.(project)
                                                        }}
                                                        sx={{ color: "rgba(0,0,0,0.7)" }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M6 2.25C5.17157 2.25 4.5 2.92157 4.5 3.75V11.25C4.5 12.0784 5.17157 12.75 6 12.75H12C12.8284 12.75 13.5 12.0784 13.5 11.25V3.75C13.5 2.92157 12.8284 2.25 12 2.25H6Z" stroke="#2666BE" strokeWidth="1.4"/>
                                                            <path d="M3.75 5.25H3C2.58579 5.25 2.25 5.58579 2.25 6V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75H10.5C10.9142 15.75 11.25 15.4142 11.25 15V14.25" stroke="#2666BE" strokeWidth="1.4" strokeLinecap="round"/>
                                                        </svg>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ mx: 1, borderColor: '#e5e5e5', height: '20px', alignSelf: 'center' }}
                                    />
                                </>
                            )}

                            {/* Info tooltip — always visible */}
                            <AdxToolTip
                                title={renderProjectInfo(project)}
                                placement="right"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: '#ffffff',
                                            color: '#000',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                            borderRadius: 3,
                                            p: 0,
                                            maxWidth: 250,
                                            border: '1px solid #e5e5e5',
                                            '& .MuiTooltip-arrow': {
                                                color: '#ffffff',
                                                '&::before': {
                                                    border: '1px solid #e5e5e5'
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        color: 'text.secondary',
                                        transition: 'color 0.2s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        pl: showActions ? 1.5 : 0,
                                        '&:hover': {
                                            color: '#2F80ED',
                                        },
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="17"
                                        height="17"
                                        viewBox="0 0 17 17"
                                        fill="currentColor"
                                    >
                                        <path d="M7.5 4.16667H9.16667V5.83333H7.5V4.16667ZM7.5 7.5H9.16667V12.5H7.5V7.5ZM8.33333 0C3.73333 0 0 3.73333 0 8.33333C0 12.9333 3.73333 16.6667 8.33333 16.6667C12.9333 16.6667 16.6667 12.9333 16.6667 8.33333C16.6667 3.73333 12.9333 0 8.33333 0ZM8.33333 15C4.65833 15 1.66667 12.0083 1.66667 8.33333C1.66667 4.65833 4.65833 1.66667 8.33333 1.66667C12.0083 1.66667 15 4.65833 15 8.33333C15 12.0083 12.0083 15 8.33333 15Z" />
                                    </svg>
                                </Box>
                            </AdxToolTip>
                        </Stack>

                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                mt: 0.75,
                                mb: 0.25,
                                minWidth: 0,
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {projectName}
                        </Typography>
                    </Stack>

                    {/* <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2, color: "rgba(0,0,0,0.75)" }}>
                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontWeight: 600 }}>Start date: {fmtDate(startDate)}</Typography>
                </Stack> */}

                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>Progress Summary</Typography>

                    <Stack gap={1.5} sx={{ mb: 4 }}>
                        <StatBar
                            leftIcon={
                                <Box
                                    component="svg"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    sx={{ width: 20, height: 20, color: "#1976d2" }}
                                >
                                    <g clipPath="url(#clip0_1496_11940)">
                                        <path
                                            d="M17.0463 2.95341L19.1412 3.37216C19.2537 3.39466 19.3463 3.47716 19.3787 3.58841C19.3952 3.64245 19.3966 3.69993 19.383 3.75474C19.3693 3.80954 19.3411 3.85963 19.3013 3.89966L17.585 5.61716C17.3794 5.82166 17.1013 5.93667 16.8113 5.93716H15.3875L11.8125 9.51341C11.8807 9.77094 11.8932 10.04 11.8493 10.3028C11.8053 10.5656 11.7059 10.8159 11.5576 11.0372C11.4093 11.2585 11.2155 11.4457 10.9892 11.5863C10.7629 11.7268 10.5092 11.8175 10.2451 11.8524C9.98099 11.8872 9.71248 11.8653 9.45747 11.7882C9.20246 11.7112 8.96681 11.5806 8.7662 11.4053C8.5656 11.23 8.40465 11.014 8.29409 10.7716C8.18353 10.5292 8.12588 10.2661 8.125 9.99966C8.12494 9.71194 8.19109 9.42807 8.31834 9.17002C8.44559 8.91196 8.63052 8.68666 8.85881 8.51154C9.0871 8.33643 9.35263 8.2162 9.63484 8.16016C9.91705 8.10413 10.2084 8.1138 10.4862 8.18841L14.0625 4.61091V3.18966C14.0625 2.89966 14.1775 2.62091 14.3825 2.41591L16.1 0.698411C16.14 0.658572 16.1901 0.630327 16.2449 0.616681C16.2997 0.603034 16.3572 0.604496 16.4112 0.620911C16.5225 0.653411 16.605 0.74591 16.6275 0.85841L17.0463 2.95341Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M2.49987 10.001C2.50115 11.0734 2.73242 12.1331 3.17808 13.1086C3.62373 14.0841 4.27341 14.9526 5.08328 15.6557C5.89315 16.3587 6.84437 16.8799 7.87279 17.184C8.90121 17.4882 9.98289 17.5683 11.0449 17.4188C12.1069 17.2694 13.1245 16.8939 14.029 16.3178C14.9336 15.7417 15.704 14.9782 16.2885 14.079C16.8729 13.1798 17.2576 12.1657 17.4168 11.1051C17.576 10.0445 17.5058 8.96213 17.2111 7.93097C17.1697 7.8105 17.1533 7.68287 17.1628 7.55584C17.1724 7.42882 17.2076 7.30507 17.2665 7.19211C17.3254 7.07916 17.4066 6.97937 17.5053 6.89882C17.604 6.81826 17.718 6.75864 17.8405 6.72357C17.9629 6.68849 18.0913 6.67871 18.2176 6.69481C18.344 6.71091 18.4657 6.75256 18.5755 6.81721C18.6852 6.88187 18.7807 6.96819 18.856 7.07091C18.9313 7.17363 18.9849 7.29061 19.0136 7.41472C19.5843 9.41163 19.4759 11.5415 18.7054 13.4701C17.935 15.3988 16.5461 17.0171 14.7566 18.0712C12.9672 19.1253 10.8784 19.5556 8.81806 19.2945C6.75769 19.0334 4.84229 18.0956 3.37237 16.6285C1.90405 15.1589 0.965339 13.2433 0.703625 11.1825C0.44191 9.12165 0.872011 7.03224 1.9264 5.24231C2.98079 3.45238 4.59975 2.0633 6.52911 1.29315C8.45848 0.522994 10.589 0.415387 12.5861 0.987221C12.8239 1.05674 13.0245 1.21756 13.1441 1.43454C13.2637 1.65152 13.2926 1.90701 13.2243 2.14519C13.1561 2.38337 12.9964 2.58486 12.7801 2.70563C12.5638 2.8264 12.3084 2.85664 12.0699 2.78972C10.9531 2.46903 9.77706 2.4122 8.63456 2.62372C7.49206 2.83524 6.41432 3.30931 5.48636 4.00856C4.5584 4.7078 3.80559 5.61309 3.28732 6.65301C2.76904 7.69293 2.49947 8.83905 2.49987 10.001Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M6.24962 9.99968C6.2497 10.6829 6.43631 11.3532 6.78933 11.9381C7.14235 12.5231 7.64838 13.0006 8.25284 13.3191C8.85729 13.6376 9.53724 13.785 10.2193 13.7455C10.9014 13.7059 11.5598 13.4809 12.1234 13.0947C12.6869 12.7075 13.1338 12.1736 13.4158 11.5508C13.6978 10.9279 13.8042 10.2398 13.7234 9.56093C13.7015 9.39618 13.724 9.22858 13.7884 9.0754C13.8529 8.92221 13.957 8.78898 14.0901 8.68941C14.2231 8.58983 14.3803 8.52753 14.5455 8.5089C14.7106 8.49027 14.8777 8.516 15.0296 8.58343C15.1815 8.64998 15.313 8.75562 15.4107 8.88957C15.5084 9.02352 15.5689 9.18099 15.5859 9.34593C15.7217 10.5061 15.493 11.6797 14.9315 12.7039C14.37 13.7282 13.5036 14.5522 12.4526 15.0617C11.4015 15.5712 10.2179 15.7408 9.06599 15.5471C7.91412 15.3533 6.85114 14.8058 6.02457 13.9805C5.19801 13.1552 4.64887 12.093 4.45338 10.9415C4.25788 9.7899 4.42572 8.60603 4.9336 7.55418C5.44149 6.50232 6.26422 5.63467 7.28761 5.07163C8.31101 4.5086 9.48429 4.27812 10.6446 4.41218C10.7689 4.4237 10.8896 4.45992 10.9996 4.5187C11.1097 4.57747 11.2069 4.65763 11.2856 4.75447C11.3643 4.85131 11.4229 4.96288 11.4579 5.08265C11.4929 5.20242 11.5036 5.32797 11.4895 5.45194C11.4753 5.57591 11.4366 5.69582 11.3755 5.80462C11.3144 5.91342 11.2322 6.00894 11.1337 6.08556C11.0353 6.16219 10.9225 6.21839 10.802 6.25086C10.6815 6.28333 10.5558 6.29143 10.4321 6.27468C9.90669 6.21367 9.3743 6.26451 8.86992 6.42386C8.36553 6.58321 7.90057 6.84747 7.50556 7.19927C7.11055 7.55107 6.79443 7.98246 6.57798 8.4651C6.36152 8.94774 6.24962 9.47072 6.24962 9.99968Z"
                                            fill="currentColor"
                                        />
                                    </g>

                                    <defs>
                                        <clipPath id="clip0_1496_11940">
                                            <rect width="20" height="20" fill="white" />
                                        </clipPath>
                                    </defs>
                                </Box>
                            }
                            tooltip="Completed"
                            valueText={firstStatText}
                            percent={firstStatPercent}
                            trackBg="rgba(25,118,210,0.15)"
                            barBg="#1f66c2"
                            height={18}
                        />
                        <StatBar
                            leftIcon={
                                <Box
                                    component="svg"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    sx={{ width: 20, height: 20, color: "#1976d2" }}
                                >
                                    <path
                                        d="M8.33352 17.3139C7.6084 17.1487 6.91189 16.8765 6.26685 16.5064M11.6668 2.68555C13.3236 3.06394 14.8029 3.99362 15.8624 5.32239C16.9219 6.65115 17.4989 8.30026 17.4989 9.99971C17.4989 11.6992 16.9219 13.3483 15.8624 14.677C14.8029 16.0058 13.3236 16.9355 11.6668 17.3139M3.81602 14.2439C3.36193 13.5832 3.01671 12.8539 2.79352 12.0839M2.60352 8.74971C2.73685 7.95805 2.99352 7.20805 3.35352 6.52055L3.49435 6.26638M5.75602 3.81555C6.53618 3.27961 7.41074 2.8962 8.33352 2.68555"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                    />
                                </Box>
                            }
                            tooltip="Ongoing"
                            valueText={String(teamPendingValue)}
                            percent={0}
                            trackBg="rgba(25,118,210,0.15)"
                            barBg="#1f66c2"
                            height={18}
                        />
                        <StatBar
                            leftIcon={
                                <Box
                                    component="svg"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    sx={{ width: 20, height: 20, color: "rgba(0,0,0,0.25)" }} // same as your icon color
                                >
                                    <path
                                        d="M13.8571 12.3143H13.0446L12.7566 12.0366C13.7995 10.827 14.3727 9.28279 14.3714 7.68571C14.3714 5.91255 13.667 4.21202 12.4132 2.9582C11.1594 1.70439 9.45888 1 7.68571 1C5.91255 1 4.21202 1.70439 2.9582 2.9582C1.70439 4.21202 1 5.91255 1 7.68571C1 9.45888 1.70439 11.1594 2.9582 12.4132C4.21202 13.667 5.91255 14.3714 7.68571 14.3714C9.34171 14.3714 10.864 13.7646 12.0366 12.7566L12.3143 13.0446V13.8571L17.4571 19L19 17.4571L13.8571 12.3143ZM7.68571 12.3143C5.11429 12.3143 3.05714 10.2571 3.05714 7.68571C3.05714 5.11429 5.11429 3.05714 7.68571 3.05714C10.2571 3.05714 12.3143 5.11429 12.3143 7.68571C12.3143 10.2571 10.2571 12.3143 7.68571 12.3143ZM5.11429 7.17143H10.2571V8.2H5.11429V7.17143Z"
                                        fill="currentColor"
                                    />
                                </Box>
                            }
                            tooltip="Remaining"
                            valueText={String(thirdStatValue)}
                            percent={0}
                            trackBg="rgba(0,0,0,0.12)"
                            barBg="rgba(0,0,0,0.25)"
                            height={18}
                        />
                    </Stack>

                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>Team Insights</Typography>

                    <Stack gap={2} sx={{ mb: 3 }}>
                        {teamInsights.length ? (
                            teamInsights.map((p) => {
                                const pct = p.total ? (p.value / p.total) * 100 : 0
                                // const isOverflow = pct > 100
                                const barColor = "#1f66c2"
                                return (
                                    <Stack key={p.id ?? p.name} direction="row" alignItems="center" gap={2}>
                                        <Box sx={{ width: 30 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Tooltip title={p.name || "Unknown"} arrow>
                                                    <Avatar
                                                        src={p.profileImageUrl || undefined}
                                                        imgProps={{
                                                            referrerPolicy: "no-referrer",
                                                        }}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor: "#F5C542",
                                                            color: "black",
                                                            fontSize: 14,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {(p.name || "?")[0]}
                                                    </Avatar>
                                                </Tooltip>
                                            </Box>

                                            {/* <Typography sx={{ mt: 1, fontWeight: 800 }} noWrap>
                                            {p.name}
                                        </Typography> */}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    borderRadius: 999,
                                                    overflow: "hidden",
                                                    backgroundColor: "rgba(25,118,210,0.15)",
                                                }}
                                            >
                                                <LinearProgress
                                                    variant="determinate"
                                                    // value={pct}
                                                    value={Math.min(100, pct)}
                                                    sx={{
                                                        height: 18,
                                                        backgroundColor: "transparent",
                                                        "& .MuiLinearProgress-bar": {
                                                            borderRadius: 999,
                                                            backgroundColor: barColor,
                                                        },
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 800,
                                                        color: "rgba(0,0,0,0.75)",
                                                    }}
                                                >
                                                    {p.display}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Stack>
                                )
                            })
                        ) : (
                            <Typography sx={{ color: "rgba(0,0,0,0.55)" }}>No team insights yet.</Typography>
                        )}
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>Tasks:</Typography>

                    <Box
                        sx={{
                            maxHeight: showMoreTasks ? '864px' : 'none',
                            overflowY: showMoreTasks ? 'auto' : 'visible',
                        }}
                    >
                        <Stack gap={1.5} sx={{ mb: 1.5 }}>
                            {tasks.length
                                ? (showMoreTasks ? tasks : tasks.slice(0, 5)).map((t) => (
                                    <TaskList key={t.id} task={t} />
                                ))
                                : <NoTasksYetCard />
                            }
                        </Stack>
                    </Box>

                    {tasks.length > 5 && (
                        <Typography
                            onClick={() => setShowMoreTasks((v) => !v)}
                            sx={{
                                color: '#2666be',
                                fontWeight: 400,
                                cursor: 'pointer',
                                textAlign: 'center',
                                mb: 2,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            {showMoreTasks ? 'View Less' : 'View More'}
                        </Typography>
                    )}

                    <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 2 }}>
                        <RadioButtonCheckedIcon
                            sx={{
                                color: isAlreadyCompleted ? "rgba(0,0,0,0.25)" : due.dotColor,
                                mt: "2px",
                            }}
                        />
                        <Box>
                            <Typography sx={{ color: "rgba(0,0,0,0.75)" }}>
                                Due date: {due.dateText}
                            </Typography>

                            {!isAlreadyCompleted && (
                                <Typography sx={{ color: due.isOverdue ? "#e53935" : "rgba(0,0,0,0.75)" }}>
                                    {due.daysLeftText}
                                </Typography>
                            )}
                        </Box>
                    </Stack>


                </CardContent>
            </Card>
        </Box>
    )
}

export default TaskProjectCard
